import { useCallback, useState } from 'react'
import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import { Decimal } from 'decimal.js'
import { Transaction } from '@mysten/sui/transactions'

import { PositionConfigInfo } from '@kunalabs-io/kai'
import { PositionCreationValues } from '../types'
import { ExecuteResult, useExecute } from 'utils/useExecute'
import { usePostHogEntityAction } from 'utils/usePostHogEntityAction'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { LP_NEW_POSITION_CREATE } from 'utils/sentry-utils'
import { usePreProcessedPositionCreate } from './usePreProcessedPositionUpdate'

type Config = ReturnType<PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>['configFromBcs']>

interface UseCreatePositionArgs {
  config: Config | undefined
  configInfo: PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  positionCreationValues: PositionCreationValues | undefined
  lpSizeUsdValue: number | null
  leverage: number
  maxLeverage: Decimal | undefined
  rangeMin: string
  rangeMax: string
  sanitizedXValue: string
  sanitizedYValue: string
  onSuccess: () => void
  onProcessed?: (newPositionId?: string) => void | Promise<void>
}

export const useCreatePosition = ({
  config,
  configInfo,
  positionCreationValues,
  lpSizeUsdValue,
  leverage,
  maxLeverage,
  rangeMin,
  rangeMax,
  sanitizedXValue,
  sanitizedYValue,
  onSuccess,
  onProcessed,
}: UseCreatePositionArgs) => {
  const [isCreatePositionLoading, setIsCreatePositionLoading] = useState(false)

  const walletAddress = useCurrentWalletAddress()
  const execute = useExecute()
  const preProcessedPositionCreate = usePreProcessedPositionCreate()
  const { capture } = usePostHogEntityAction()

  const onCreatePosition = useCallback(async () => {
    if (!config || !positionCreationValues || !walletAddress || isCreatePositionLoading) {
      return
    }

    setIsCreatePositionLoading(true)

    let newPositionId: string | undefined

    void execute(
      () => {
        const tx = new Transaction()
        config.createPositionFromWallet(
          tx,
          {
            liquidity: positionCreationValues.liquidity,
            tickA: positionCreationValues.tickA,
            tickB: positionCreationValues.tickB,
            UX: positionCreationValues.UX,
            UY: positionCreationValues.UY,
          },
          walletAddress
        )
        return tx
      },
      {
        transactionType: LP_NEW_POSITION_CREATE,
        sentryContext: {
          walletAddress,
          xInputValue: sanitizedXValue,
          yInputValue: sanitizedYValue,
          rangeMax,
          rangeMin,
          leverage,
          maxLeverage: maxLeverage?.toString(),
          liquidity: positionCreationValues.liquidity,
          tickA: positionCreationValues.tickA,
          tickB: positionCreationValues.tickB,
          UX: positionCreationValues.UX,
          UY: positionCreationValues.UY,
        },
        onSuccess: async (result: ExecuteResult) => {
          newPositionId = await preProcessedPositionCreate(result.createdObjects)

          const positionCreationValuesLeverage = positionCreationValues?.info.leverage.toNumber()

          capture('lp:create_position', {
            config_id: configInfo.configId,
            config_name: configInfo.name,
            leverage: positionCreationValuesLeverage ? Math.round(positionCreationValuesLeverage * 100) / 100 : null,
            equity_usd:
              typeof lpSizeUsdValue === 'number' &&
              typeof positionCreationValuesLeverage === 'number' &&
              positionCreationValuesLeverage !== 0
                ? Math.round((lpSizeUsdValue / positionCreationValuesLeverage) * 100) / 100
                : null,
          })

          setIsCreatePositionLoading(false)
          onSuccess()
        },
        onProcessed: async () => {
          await onProcessed?.(newPositionId)
        },
        onError: () => {
          setIsCreatePositionLoading(false)
        },
      }
    )
  }, [
    config,
    positionCreationValues,
    walletAddress,
    execute,
    isCreatePositionLoading,
    sanitizedXValue,
    sanitizedYValue,
    rangeMax,
    rangeMin,
    leverage,
    maxLeverage,
    capture,
    configInfo.configId,
    configInfo.name,
    lpSizeUsdValue,
    onSuccess,
    onProcessed,
    preProcessedPositionCreate,
  ])

  return {
    onCreatePosition,
    isCreatePositionLoading,
  }
}
